import React from 'react';
import { Check } from 'lucide-react';
import './ReminderPillGroup.css';

/**
 * F0 — Reminder pill group primitive.
 *
 * Props:
 *   - options: Array<{ key: string, label: string, recommended?: boolean }>
 *   - selectedKeys: Set<string>
 *   - onToggle: (key: string) => void
 *   - disabled
 *   - ariaLabel
 *
 * Renders an outline pill per option. Active pills have a green-tinted
 * background and show a Check icon. Options flagged `recommended: false`
 * render with a dashed outline to visually de-emphasize them (the 4
 * recommended options from the mockup get the solid outline).
 */
export const formatSelectedSubtitle = (count) =>
  `${count} selected`;

const ReminderPillGroup = ({
  options = [],
  selectedKeys,
  onToggle,
  disabled = false,
  ariaLabel = 'Reminder offsets',
}) => {
  const isSelected = (key) => {
    if (!selectedKeys) return false;
    if (typeof selectedKeys.has === 'function') return selectedKeys.has(key);
    if (Array.isArray(selectedKeys)) return selectedKeys.includes(key);
    return false;
  };

  return (
    <div
      className={`appointments-v3-reminder-group ${disabled ? 'is-disabled' : ''}`}
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((opt) => {
        const active = isSelected(opt.key);
        const recommended = opt.recommended !== false;
        return (
          <button
            key={opt.key}
            type="button"
            className={[
              'appointments-v3-reminder-pill',
              active ? 'is-active' : '',
              recommended ? 'is-recommended' : 'is-extra',
            ].filter(Boolean).join(' ')}
            aria-pressed={active}
            disabled={disabled}
            onClick={() => onToggle?.(opt.key)}
          >
            {active && (
              <Check
                size={14}
                strokeWidth={2.5}
                className="appointments-v3-reminder-pill-check"
                aria-hidden="true"
              />
            )}
            <span className="appointments-v3-reminder-pill-label">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
};

export default ReminderPillGroup;
